import { Feature, LineString, Position } from "geojson";

import { MapboxDrawBase } from "fond/types";

import { buildEndpointIndex, findFeaturesAtEndpoint } from "./modes/chainSelection";

/** Tolerance in degrees (~0.11m at equator), matching chainSelection convention */
const ENDPOINT_TOLERANCE = 0.000001;

/**
 * After path CostFactors are updated, syncs CostFactor onto colocated
 * inStreetPoint features.
 *
 * For each updated path's endpoints, finds colocated Point features, then
 * determines the most common CostFactor among all paths attached to that
 * point. Defaults to 1 if there are no attached paths or a tie.
 *
 * @returns The array of Point features that were updated (via
 *          setFeatureProperty on drawControl). Callers are responsible for
 *          triggering a redraw (e.g. firing a draw.update event).
 */
export function syncPointCostFactors(drawControl: MapboxDrawBase, updatedPaths: Feature[]): Feature[] {
  const allFeatures = drawControl.getAll().features;

  // Collect unique endpoint coordinates from updated paths
  const endpoints: Position[] = updatedPaths
    .filter((path): path is Feature<LineString> => path.geometry?.type === "LineString")
    .flatMap((path) => {
      const coords = path.geometry.coordinates;
      return coords.length >= 2 ? [coords[0], coords[coords.length - 1]] : [];
    });

  if (endpoints.length === 0) return [];

  // Find all Point features colocated with updated path endpoints
  const pointFeatures: Feature[] = [];
  const seenPointIds = new Set<string | number>();

  for (const feature of allFeatures) {
    const pointId = feature.id ?? feature.properties?.id;
    if (feature.geometry?.type === "Point" && pointId != null && !seenPointIds.has(pointId)) {
      const [pLng, pLat] = (feature.geometry as GeoJSON.Point).coordinates;
      const isColocated = endpoints.some((ep) => Math.abs(pLng - ep[0]) < ENDPOINT_TOLERANCE && Math.abs(pLat - ep[1]) < ENDPOINT_TOLERANCE);
      if (isColocated) {
        seenPointIds.add(pointId);
        pointFeatures.push(feature);
      }
    }
  }

  if (pointFeatures.length === 0) return [];

  // Build endpoint index from ALL paths so we can find every path attached to a point
  const { tree, featureMap } = buildEndpointIndex(allFeatures);

  const updatedPoints: Feature[] = [];

  for (const pointFeature of pointFeatures) {
    const pointId = pointFeature.id ?? pointFeature.properties?.id;
    if (pointId != null) {
      const pointCoords = (pointFeature.geometry as GeoJSON.Point).coordinates;

      // Find all paths whose endpoints are colocated with this point
      const attachedPathIds = findFeaturesAtEndpoint(tree, pointCoords);

      // Compute the most common CostFactor among attached paths
      const costFactor = getMostCommonCostFactor(attachedPathIds, featureMap);

      drawControl.setFeatureProperty(pointId as string, "CostFactor", costFactor);
      const updated = drawControl.get(pointId as string);
      if (updated) {
        updatedPoints.push(updated);
      }
    }
  }

  return updatedPoints;
}

/**
 * Returns the most common CostFactor among the given path IDs.
 * Defaults to 1 if no paths, all have undefined CostFactor, or there is a tie.
 */
function getMostCommonCostFactor(pathIds: string[], featureMap: Map<string, Feature<LineString>>): number {
  const counts = new Map<number, number>();

  for (const id of pathIds) {
    const feature = featureMap.get(id);
    if (feature) {
      const raw = feature.properties?.CostFactor ?? feature.properties?.user_CostFactor;
      const cost = raw == null ? 1 : Number(raw);
      if (!Number.isNaN(cost)) {
        counts.set(cost, (counts.get(cost) ?? 0) + 1);
      }
    }
  }

  if (counts.size === 0) return 1;

  let maxCount = 0;
  let mode: number = 1;
  let tied = false;

  for (const [value, count] of counts) {
    if (count > maxCount) {
      maxCount = count;
      mode = value;
      tied = false;
    } else if (count === maxCount) {
      tied = true;
    }
  }

  return tied ? 1 : mode;
}
